import {
  FormControl,
  FormLabel,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';
import Button from '@mui/joy/Button';
import { SelectTrigger } from '@radix-ui/react-select';

import { partition } from 'lodash-es';
import { useState } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { MultiSelect } from '@/components/MultiSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/Select/Select';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import {
  useCurrentOrganization,
  useInviteWorkspaceMemberMutation,
  useInviteWorkspaceMembersBatchMutation,
  useRoles,
  useSelectedWorkspace,
} from '@/hooks/useSwr';
import InviteUserIcon from '@/icons/InviteUserIcon.svg?react';
import { PostWorkspaceMemberBody, TenantSchema } from '@/types/schema';

import {
  MemberGroupType,
  MemberStatus,
} from '../types/organizationMembers.type';
import { MEMBERS_LIMIT } from '../utils/constants';
import { useMembers } from '../utils/useMembers';
import { BulkEmailAddressInput } from './BulkEmailAddressInput';
import { UpsellBadge } from './UpsellBadge';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

function RoleSelect(props: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const rolesQuery = useRoles('workspace');
  const roles = rolesQuery.data;

  const organizationQuery = useCurrentOrganization();
  const isRbacEnabled = organizationQuery.data?.config.can_use_rbac;

  return (
    <Select
      value={props.value}
      onValueChange={props.onChange}
      disabled={!isRbacEnabled}
    >
      <SelectTrigger
        className="rounded-md border border-secondary p-2 px-2.5 text-left data-[placeholder]:text-tertiary"
        disabled={!isRbacEnabled}
      >
        <SelectValue placeholder="Select role..." />
      </SelectTrigger>
      <SelectContent>
        {roles?.map((role) => {
          return (
            <SelectItem key={role.id} value={role.id}>
              <div className="flex items-center gap-2">{role.display_name}</div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function InputUsers(props: {
  memberCount: number;
  emails: string[];
  onChange: (emails: string[]) => void;
  setValidationError: (error: string | null) => void;
}) {
  const organizationSwr = useCurrentOrganization();

  let maxSeats = organizationSwr.data?.config.max_identities ?? 1;
  if (typeof maxSeats !== 'number') maxSeats = parseInt(maxSeats);

  return (
    <BulkEmailAddressInput
      value={props.emails}
      maxMemberCount={maxSeats}
      memberCount={props.memberCount}
      onChange={props.onChange}
      label="Emails"
    />
  );
}

function SelectUsers(props: {
  emails: string[];
  onChange: (emails: string[]) => void;
  setValidationError: (error: string | null) => void;
}) {
  const {
    members: { data: orgMembers, isLoading: orgLoading },
    total: orgTotal,
  } = useMembers(MemberStatus.ACTIVE, MemberGroupType.ORGANIZATION);

  const {
    members: { data: workspaceMembers, isLoading: workspaceLoading },
  } = useMembers(MemberStatus.ACTIVE, MemberGroupType.WORKSPACE);

  const filteredOrgMembers =
    orgMembers
      ?.filter(
        (member) => !workspaceMembers?.some((m) => m.email === member.email)
      )
      .map((m) => ({ id: m.id, display: m.email || '' })) || [];

  if (orgLoading || workspaceLoading) {
    return <LinearProgress />;
  }

  const handleEmailChange = (newEmails: string[]) => {
    props.setValidationError(null);
    props.onChange(newEmails);
  };

  if (orgTotal > MEMBERS_LIMIT) {
    // Limit to 50 email inputs at a time because that's our pagination size to check
    // if a user exists in the org or not. This way we can partition correctly when inviting
    // user to both workspace and org.
    return (
      <BulkEmailAddressInput
        value={props.emails}
        maxMemberCount={MEMBERS_LIMIT}
        memberCount={0}
        onChange={handleEmailChange}
        label="Emails"
      />
    );
  }

  return (
    <MultiSelect
      emptyState={
        'No members found. Invite them to the organization before adding them to the workspace.'
      }
      value={props.emails}
      onChange={handleEmailChange}
      autocompleteList={filteredOrgMembers}
      validate={(email) => {
        return (
          filteredOrgMembers.some((m) => m.display === email) &&
          !props.emails.find((e) => e === email) &&
          !!email.trim() &&
          email.match(EMAIL_REGEX) !== null
        );
      }}
    />
  );
}

function WorkspaceMemberForm(props: {
  workspace: TenantSchema;
  onComplete?: () => void;
}) {
  const inviteMembersMutation = useInviteWorkspaceMembersBatchMutation();
  const inviteMemberMutation = useInviteWorkspaceMemberMutation();

  const canInviteToOrg = useInstanceFlag(
    InstanceFlags.workspace_scope_org_invites
  );

  const [validationError, setValidationError] = useState<string | null>(null);

  const organizationSwr = useCurrentOrganization();

  const [emails, setEmails] = useState<string[]>([]);
  const [roleId, setRoleId] = useState<string | undefined>(undefined);

  const {
    members: { data: orgMembers },
    total: orgTotal,
  } = useMembers(MemberStatus.ACTIVE, MemberGroupType.ORGANIZATION, undefined, {
    emails: emails,
  });

  const error = validationError ?? inviteMembersMutation.error;

  const isRbacEnabled = organizationSwr.data?.config.can_use_rbac;

  if (organizationSwr.isLoading) {
    return <LinearProgress />;
  }

  return (
    <form
      className="flex flex-col items-stretch gap-8"
      onSubmit={async (e) => {
        e.preventDefault();
        const nEmails = emails.length;
        if (nEmails === 0) {
          setValidationError('At least one email is required');
          return;
        } else {
          setValidationError(null);
        }

        try {
          const [existingMembers, newMembers] = partition(emails, (email) =>
            orgMembers?.some((member) => member.email === email)
          );

          const addToWorkspaceMembers = existingMembers
            .map((e) => ({
              user_id: orgMembers?.find((m) => m.email === e)?.user_id,
              role_id: roleId,
            }))
            .filter(
              (m) => m.user_id !== undefined
            ) as PostWorkspaceMemberBody[];
          if (canInviteToOrg) {
            const inviteToOrgMembers = newMembers.map((e) => ({
              email: e,
              workspace_role_id: roleId,
            }));
            if (inviteToOrgMembers.length > 0) {
              await inviteMembersMutation.trigger({ json: inviteToOrgMembers });
            }
          } else if (newMembers.length > 0) {
            const membersNotInOrg = newMembers.join(', ');
            throw new Error(
              `Cannot add the following users to the workspace. Please add them to the organization first: ${membersNotInOrg}`
            );
          }

          if (addToWorkspaceMembers.length > 0) {
            await inviteMemberMutation.trigger(addToWorkspaceMembers);
          }
          props.onComplete?.();
        } catch (e: unknown) {
          setValidationError((e as Error).message);
        }
      }}
    >
      <div className="flex flex-col items-stretch gap-6">
        {canInviteToOrg ? (
          <InputUsers
            memberCount={orgTotal}
            emails={emails}
            onChange={setEmails}
            setValidationError={setValidationError}
          />
        ) : (
          <SelectUsers
            emails={emails}
            onChange={setEmails}
            setValidationError={setValidationError}
          />
        )}
      </div>

      <FormControl>
        <div className="flex flex-col">
          <FormLabel className={'gap-2'}>
            Add as
            {!isRbacEnabled && (
              <UpsellBadge
                tooltipMsg={
                  'Role based access control is only available for enterprise customers. Contact our sales team for more'
                }
                tier="ENTERPRISE"
                linkProps={{
                  to: 'https://www.langchain.com/contact-sales',
                  target: '_blank',
                }}
              />
            )}
          </FormLabel>
          <RoleSelect value={roleId} onChange={setRoleId} />
        </div>
      </FormControl>

      {error && <ExpandableErrorAlert error={error} />}

      <div className="flex items-center justify-end">
        <Button
          type="submit"
          loading={
            inviteMembersMutation.isMutating || inviteMemberMutation.isMutating
          }
          disabled={emails.length === 0}
        >
          Add members
        </Button>
      </div>
    </form>
  );
}

export function WorkspaceMemberModal() {
  const [open, setOpen] = useState(false);

  const workspace = useSelectedWorkspace();
  const canInviteToOrg = useInstanceFlag(
    InstanceFlags.workspace_scope_org_invites
  );

  if (!workspace.data) return null;
  const inviteDesc = `Add existing members ${
    canInviteToOrg ? `to` : `of your organization to`
  } ${workspace.data.display_name}`;

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          sx={{
            minWidth: 550,
            maxWidth: 600,
            maxHeight: '90%',
            overflowY: 'auto',
          }}
        >
          <ModalClose size="lg" />
          <div className="flex w-full flex-col px-4">
            <h2 className="text-lg font-semibold">Add members to workspace</h2>
            <h4 className="mb-4 text-sm font-normal text-tertiary">
              {inviteDesc}
            </h4>

            <WorkspaceMemberForm
              workspace={workspace.data}
              onComplete={() => setOpen(false)}
            />
          </div>
        </ModalDialog>
      </Modal>

      <Button
        size="sm"
        color="primary"
        onClick={() => setOpen(true)}
        startDecorator={<InviteUserIcon />}
      >
        Add members
      </Button>
    </>
  );
}
